import {AxiosResponse} from 'axios';
import {adminAPI, authAdminAPI} from '@/core/api';
import {API_URL} from '../enums/api-url.enum';
import {
  IAdminAllEventsResponse,
  IAdminAllOrganizationsResponse,
  IAdminAllTransactionsResponse,
  IAdminInfo,
  IAdminLogin2FactorRequest,
  IAdminLoginRequest,
  IAdminLoginResponse,
  IAdminOrganizationResponse,
  IAdminPercentage,
  IAdminStatisticResponse,
  IEventsListRequest,
  IEventStatus,
  IOrganizationPercentage,
  IOrganizationsListRequest,
  ITransactionsListRequest,
  ITwoFactorConfirmResponse,
  ITwoFactorSetupResponse,
} from '../types/superAdmin.interface';

class SuperAdminService {
  static login = async (reqBody: IAdminLoginRequest): Promise<AxiosResponse<IAdminLoginResponse>> =>
    authAdminAPI.post(API_URL.SUPER_ADMIN_LOGIN, reqBody);

  static getInfo = async (id: number): Promise<AxiosResponse<IAdminInfo>> =>
    adminAPI.get(`${API_URL.SUPER_ADMIN_INFO}/${id}`);

  static changeProfile = async (reqBody: IAdminInfo): Promise<AxiosResponse<IAdminLoginResponse>> => {
    return adminAPI.put(`${API_URL.SUPER_ADMIN_INFO}/${reqBody.id}`, reqBody);
  };

  static getAllOrganizations = async (
    data: IOrganizationsListRequest
  ): Promise<AxiosResponse<IAdminAllOrganizationsResponse>> => adminAPI.post(API_URL.ADMIN_ALL_ORGANIZATIONS, data);

  static getOrganizationById = async (id: number): Promise<AxiosResponse<IAdminOrganizationResponse>> =>
    adminAPI.post(`${API_URL.ADMIN_ORGANIZATION_BY_ID}?id=${id}`);

  static getAllOrganizationsTransactions = async (
    data: ITransactionsListRequest
  ): Promise<AxiosResponse<IAdminAllTransactionsResponse>> =>
    adminAPI.post(API_URL.ADMIN_ORGANIZATION_TRANSACTIONS, data);

  static getAllEvents = async (data: IEventsListRequest): Promise<AxiosResponse<IAdminAllEventsResponse>> =>
    adminAPI.post(API_URL.ADMIN_ALL_EVENTS, data);

  static getAdminStatistic = async (statisticsTerm: string): Promise<AxiosResponse<IAdminStatisticResponse>> =>
    adminAPI.get(`${API_URL.ADMIN_STATISTIC}/${statisticsTerm}`);

  static updateOrganizationStatus = async ({
    id,
    newStatus,
  }: {
    id: number;
    newStatus: number;
  }): Promise<AxiosResponse<number>> => adminAPI.put(`${API_URL.ADMIN_STATUS_UPDATE}?id=${id}&newStatus=${newStatus}`);

  static updateEventStatus = async ({id, status}: {id: number; status: IEventStatus}): Promise<AxiosResponse<number>> =>
    adminAPI.put(`${API_URL.EVENT}/${id}/status?status=${status}`);

  static getDefaultPlatformFee = async (): Promise<AxiosResponse<IAdminPercentage>> =>
    adminAPI.get(API_URL.ADMIN_PLATFORM_FEE);

  static setDefaultPlatformFee = async (data: IAdminPercentage): Promise<AxiosResponse> =>
    adminAPI.put(API_URL.ADMIN_PLATFORM_FEE, data);

  static setOrganizationPlatformFee = async (id: number, data: IOrganizationPercentage): Promise<AxiosResponse> =>
    adminAPI.put(`${API_URL.ADMIN_ORG}/${id}/transaction-fee`, data);

  static getAdmin2FactorStatus = async (): Promise<AxiosResponse<{isEnabled: boolean}>> =>
    adminAPI.get(API_URL.ADMIN_2FACTOR_STATUS);

  static setAdmin2FactorSetup = async ({
    password,
  }: {
    password: string;
  }): Promise<AxiosResponse<ITwoFactorSetupResponse>> => adminAPI.post(API_URL.ADMIN_2FACTOR_SETUP, {password});

  static confirmAdmin2FactorSetup = async ({code}: {code: string}): Promise<AxiosResponse<ITwoFactorConfirmResponse>> =>
    adminAPI.post(API_URL.ADMIN_2FACTOR_CONFIRM, {code});

  static setAdmin2FactorDisabled = async ({
    code,
    password,
  }: {
    code: string;
    password: string;
  }): Promise<AxiosResponse> => adminAPI.post(API_URL.ADMIN_2FACTOR_DISABLE, {code, password});

   static login2Factor = async (reqBody: IAdminLogin2FactorRequest): Promise<AxiosResponse<IAdminLoginResponse>> =>
    authAdminAPI.post(API_URL.SUPER_ADMIN_LOGIN_2FACTOR, reqBody);
}

export {SuperAdminService};
